// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Deal } from '@interfaces/deal.interfaces';
import { DdjRouteOneErrorCodes } from '@interfaces/document.interfaces';
import { VendorService } from '@services/vendor.service';
import { catchError, takeWhile } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { showFormErrors } from 'src/app/utils/form-utils';
import { DealershipService } from '../../services/dealership.service';
import { SettingsService } from '@services/settings.service';

@Component({
    selector: 'app-r1-user-data-modal',
    templateUrl: './r1-user-data-modal.component.html',
    styleUrls: ['./r1-user-data-modal.component.scss'],
})
export class R1UserDataModalComponent implements OnInit, OnDestroy {
    private isComponentAlive = true;
    public isLoading = false;
    public deal: Deal;
    public routeOneResponse: any;
    public r1DealerId: any;
    public userUpdateGroup: FormGroup;
    public supportEmail: string;
    public supportPhone: string;

    constructor(
        private dialogRef: MatDialogRef<R1UserDataModalComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private readonly vendorService: VendorService,
        private fb: FormBuilder,
        private readonly dealershipService: DealershipService,
        private readonly settingsService: SettingsService
    ) {}

    ngOnInit() {
        this.deal = this.data.deal;
        this.r1DealerId = this.data.r1DealerId;
        this.dealershipService.dealer$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(async (dealer) => {
            ({ supportEmail: this.supportEmail, supportPhone: this.supportPhone } = await this.settingsService.getOemSettings(dealer.oemCode));
        });

        this.buildForm();
    }

    private buildForm(): void {
        this.userUpdateGroup = this.fb.group({
            dealershipId: ['', Validators.compose([Validators.required, Validators.maxLength(5)])],
        });
    }

    private isValid(inputR1DealerShip: any): boolean {
        let errors = false;

        if (this.userUpdateGroup.status === 'INVALID') {
            showFormErrors(this.userUpdateGroup.controls);
            errors = true;
        } else if (this.r1DealerId !== undefined && this.r1DealerId !== null) {
            //Prevent changing the dealerId if there is one already.
            if (this.r1DealerId !== inputR1DealerShip) {
                errors = true;
            }
        }

        return !errors;
    }

    updateR1UserInfo() {
        const dealershipId = this.userUpdateGroup.controls['dealershipId'].value;
        if (!this.isValid(dealershipId)) {
            return;
        }

        this.isLoading = true;
        this.vendorService
            .updateRouteOneCredentials(
                this.deal.dealerId,
                this.deal.dealId,
                this.deal.buyer.firstName,
                this.deal.buyer.lastName,
                this.deal.vehicle.vin,
                dealershipId
            )
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((response) => {
                if (response.error) {
                    const errorTitle = 'Route One Error';
                    let errorMessage = 'Could not update account information with RouteOne. Plase try again.';

                    if (response.error.name?.includes(DdjRouteOneErrorCodes.EnrolledHasBadCreds.valueOf().toString(), 0)) {
                        errorMessage = response.error.message;
                    } else if (response.error.name?.includes(DdjRouteOneErrorCodes.EnrolledMissingDealerId.valueOf().toString(), 0)) {
                        errorMessage = response.error.message;
                    } else if (response.error.name?.includes(DdjRouteOneErrorCodes.BadAuditCreds.valueOf().toString(), 0)) {
                        errorMessage = response.error.message;
                    }

                    this.routeOneResponse = {
                        error: true,
                        title: errorTitle,
                        message: response.error.message
                            ? `${response.error.message} Support Information: Email: ${this.supportEmail}, Phone: ${this.supportPhone}`
                            : `${errorMessage} Support Information: Email: ${this.supportEmail}, Phone: ${this.supportPhone}`,
                    };
                } else if (response?.data === true) {
                    this.dialogRef.close({ refresh: true });
                }
                this.isLoading = false;
            });
    }

    close() {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
